import { Flex, Text } from '@chakra-ui/react'
import React, { ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'
import { ContactSourceType } from '@mangovoice/types'

import { CallContactButton } from './CallContactButton'
import { useContactCardStyles } from './componentStyles/ContactCard.style'

interface ContactCardProps {
  contact: ContactDetails
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
  isRecentDialed?: boolean
}

export const ContactCard = ({
  contact,
  onStartCall,
  isRecentDialed,
}: ContactCardProps) => {
  const { t } = useTranslation()
  const styles = useContactCardStyles()

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    gap: styles.container?.gap,
    alignItems: styles.container?.alignItems,
    justifyContent: styles.container?.justifyContent,
  }

  const labelContainerProps = {
    flexDirection: styles.labelContainer?.flexDirection,
  }

  const handleStartCall = useCallback(
    (number: ContactNumber) => {
      onStartCall({ contactNumber: number, contact })
    },
    [contact, onStartCall],
  )

  const getNumberComponents = useCallback(() => {
    let label: ReactNode
    if (contact.numbers === undefined) {
      // No numbers for contact, no label/no button
      label = ''
      return { label, button: (<></>) as React.ReactElement }
    } else {
      if (contact.source === ContactSourceType.Extensions) {
        // Extension, need ExtensionBadge/no label
        label = (
          <ExtensionBadge
            size={Size.lg}
            extension={contact.defaultNumber.number}
          />
        )
      } else if (contact.id === -1) {
        // Non-contact number from search
        label = (
          <Text css={styles.nameText} lineClamp={1}>
            {t('callContactButton.callNumber', {
              number: formatPhoneNumber(contact.defaultNumber.number),
            })}
          </Text>
        )
      } else {
        // Regular contact
        label = (
          <Text css={styles.numberText} lineClamp={1}>
            {`${toNounCasing(
              contact.defaultNumber?.label ?? '',
            )}: ${formatPhoneNumber(contact.defaultNumber?.number)}`}
          </Text>
        )
      }
      return {
        label,
        button: (
          <CallContactButton
            contactName={contact.name || undefined}
            defaultContactNumber={contact.defaultNumber}
            otherContactNumbers={contact.numbers}
            onStartCall={handleStartCall}
            isRecentDialed={isRecentDialed}
          />
        ),
      }
    }
  }, [
    contact,
    handleStartCall,
    isRecentDialed,
    styles.nameText,
    styles.numberText,
    t,
  ])

  const { label: Label, button: Button } = getNumberComponents()

  return (
    <Flex css={styles.container} {...containerProps}>
      <Flex css={styles.labelContainer} {...labelContainerProps}>
        <Text css={styles.nameText} lineClamp={1} marginRight={2}>
          {contact.name}
        </Text>
        {Label}
      </Flex>
      {Button}
    </Flex>
  )
}
